import { z } from 'zod';
import { EXECUTION_MODES, STRATEGY_KEYS } from '../enums';
import { decimalStringSchema } from './common';

/** PnL and equity statistics (API_DOCS.md — Settings, Stats, Audit). */

export const statsQuerySchema = z.object({
  mode: z.enum(EXECUTION_MODES).optional(),
  /** '7d' | '30d' | '90d' | 'all' */
  range: z
    .string()
    .regex(/^(?:\d{1,4}d|all)$/)
    .default('30d'),
});
export type StatsQuery = z.infer<typeof statsQuerySchema>;

export const dailyPnlPointSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  realizedPnl: decimalStringSchema,
  fees: decimalStringSchema,
  trades: z.number().int().nonnegative(),
  wins: z.number().int().nonnegative(),
});
export type DailyPnlPoint = z.infer<typeof dailyPnlPointSchema>;

export const pnlStatsResponseSchema = z.object({
  daily: z.array(dailyPnlPointSchema),
  totalRealized: decimalStringSchema,
  winRate: decimalStringSchema,
  avgWin: decimalStringSchema,
  avgLoss: decimalStringSchema,
  profitFactor: decimalStringSchema.nullable(),
  maxDrawdownPct: decimalStringSchema,
});
export type PnlStatsResponse = z.infer<typeof pnlStatsResponseSchema>;

export const equityStatsResponseSchema = z.object({
  points: z.array(
    z.object({
      ts: z.string().datetime(),
      equity: decimalStringSchema,
    }),
  ),
});
export type EquityStatsResponse = z.infer<typeof equityStatsResponseSchema>;

/**
 * GET /signals/rejections — why the bot is not trading: rejected signals in the last `days`,
 * counted by reason (the part of `rejection_reason` before ": ") and strategy.
 */
export const rejectionsQuerySchema = z.object({
  mode: z.enum(EXECUTION_MODES).optional(),
  days: z.coerce.number().int().min(1).max(90).default(7),
});
export type RejectionsQuery = z.infer<typeof rejectionsQuerySchema>;

export const rejectionCountSchema = z.object({
  reason: z.string(),
  strategyKey: z.enum(STRATEGY_KEYS),
  count: z.number().int().nonnegative(),
});
export type RejectionCount = z.infer<typeof rejectionCountSchema>;

export const rejectionsResponseSchema = z.object({
  mode: z.enum(EXECUTION_MODES),
  days: z.number().int(),
  total: z.number().int().nonnegative(),
  /** Largest count first. */
  rows: z.array(rejectionCountSchema),
});
export type RejectionsResponse = z.infer<typeof rejectionsResponseSchema>;
